import { apiClient } from './client'
import type { CashRegisterSettlement, CreateEventPayload, Event, EventStats, EventVendor } from '@/types/event'

interface PaginatedResponse<T> {
  data: T[]
  meta: {
    current_page: number
    per_page: number
    total: number
    last_page: number
  }
}

export const eventsApi = {
  list: async (params?: {
    page?: number
    per_page?: number
    status?: string
  }): Promise<PaginatedResponse<Event>> => {
    const { data } = await apiClient.get('/admin/events', { params })
    return { data: data.data, meta: data.meta }
  },

  get: async (id: number): Promise<Event> => {
    const { data } = await apiClient.get(`/admin/events/${id}`)
    return data.data
  },

  create: async (payload: CreateEventPayload): Promise<Event> => {
    const { data } = await apiClient.post('/admin/events', payload)
    return data.data
  },

  update: async (id: number, payload: Partial<CreateEventPayload>): Promise<Event> => {
    const { data } = await apiClient.put(`/admin/events/${id}`, payload)
    return data.data
  },

  publish: async (id: number): Promise<{ public_url: string; published_at: string }> => {
    const { data } = await apiClient.post(`/admin/events/${id}/publish`)
    return data.data
  },

  close: async (id: number): Promise<Event> => {
    const { data } = await apiClient.post(`/admin/events/${id}/close`)
    return data.data
  },

  stats: async (id: number): Promise<EventStats> => {
    const { data } = await apiClient.get(`/admin/events/${id}/stats`)
    return data.data
  },

  addTicketType: async (eventId: number, payload: {
    name: string; description?: string; price: number; capacity: number; sort_order?: number
  }) => {
    const { data } = await apiClient.post(`/admin/events/${eventId}/ticket-types`, payload)
    return data.data
  },

  toggleTicketType: async (eventId: number, typeId: number) => {
    const { data } = await apiClient.put(`/admin/events/${eventId}/ticket-types/${typeId}/toggle`)
    return data.data
  },

  deleteTicketType: async (eventId: number, typeId: number) => {
    await apiClient.delete(`/admin/events/${eventId}/ticket-types/${typeId}`)
  },

  listVendors: async (eventId: number): Promise<EventVendor[]> => {
    const { data } = await apiClient.get(`/admin/events/${eventId}/vendors`)
    return data.data
  },

  assignVendor: async (eventId: number, userId: number): Promise<void> => {
    await apiClient.post(`/admin/events/${eventId}/vendors`, { user_id: userId })
  },

  removeVendor: async (eventId: number, userId: number): Promise<void> => {
    await apiClient.delete(`/admin/events/${eventId}/vendors/${userId}`)
  },

  vendorStats: async (eventId: number) => {
    const { data } = await apiClient.get(`/admin/events/${eventId}/vendor-stats`)
    return data.data
  },

  vendorBalance: async (eventId: number, vendorId: number): Promise<{ tickets_sold: number; total_amount: number }> => {
    const { data } = await apiClient.get(`/admin/events/${eventId}/vendors/${vendorId}/balance`)
    return data.data
  },

  listSettlements: async (eventId: number): Promise<CashRegisterSettlement[]> => {
    const { data } = await apiClient.get(`/admin/events/${eventId}/settlements`)
    return data.data
  },

  settle: async (eventId: number, vendorId: number, payload: { actual_amount: number; notes?: string }): Promise<CashRegisterSettlement> => {
    const { data } = await apiClient.post(`/admin/events/${eventId}/vendors/${vendorId}/settle`, payload)
    return data.data
  },
}
